import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { 
  MessageCircle,
  Phone,
  HelpCircle,
  Clock,
  Shield,
  Heart,
  Zap,
  Users,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react';

const FAQ = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-soft overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-6 max-w-4xl mx-auto">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center shadow-elegant">
                <HelpCircle className="h-8 w-8 text-white" />
              </div>
            </div>
            <h1 className="text-hero text-foreground">Frequently Asked Questions</h1>
            <p className="text-xl text-primary font-medium">Everything you need to know about our treatments and services</p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Find answers to common questions about our wellness treatments, booking process, and what to expect 
              during your visit to Glow by Precious.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                <Button className="gradient-primary text-white shadow-elegant hover:shadow-lg transition-all duration-300 w-full sm:w-auto">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Ask a Question
                </Button>
              </a>
              <a href="tel:0695523598">
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white transition-smooth w-full sm:w-auto">
                  <Phone className="h-4 w-4 mr-2" />
                  Call: 069 552 3598
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-6 text-center space-y-3">
                <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center mx-auto">
                  <Zap className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-foreground">Treatments</h3>
                <p className="text-sm text-muted-foreground">About our services</p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-6 text-center space-y-3">
                <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center mx-auto">
                  <Clock className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-foreground">Booking</h3>
                <p className="text-sm text-muted-foreground">Scheduling & appointments</p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-6 text-center space-y-3">
                <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center mx-auto">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-foreground">Safety</h3>
                <p className="text-sm text-muted-foreground">Health & safety info</p>
              </CardContent>
            </Card>
            
            <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0 bg-gradient-to-br from-white to-pink-50">
              <CardContent className="p-6 text-center space-y-3">
                <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center mx-auto">
                  <Heart className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-foreground">Results</h3>
                <p className="text-sm text-muted-foreground">What to expect</p>
              </CardContent>
            </Card>
          </div>

          {/* FAQ Accordion */}
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              
              {/* General Questions */}
              <AccordionItem value="item-1" className="border border-border rounded-lg px-6">
                <AccordionTrigger className="text-left hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Info className="h-5 w-5 text-primary" />
                    <span className="font-semibold">What services does Glow by Precious offer?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-muted-foreground leading-relaxed">
                  We offer a comprehensive range of beauty, health, and wellness treatments including:
                  <ul className="mt-3 space-y-2 ml-4">
                    <li>• Body Contouring (cavitation, RF, laser, vacuum bipolar RF, wood therapy)</li>
                    <li>• Slimming & Glow Injections for targeted fat reduction and skin radiance</li>
                    <li>• IV Drip Therapy (weight loss, energy, glow, anti-aging formulations)</li>
                    <li>• Therapeutic Massage (relaxation, detox, rejuvenation)</li>
                    <li>• Butt Vacuum enhancement therapy</li>
                    <li>• Medical Weight Loss programs with professional guidance</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="border border-border rounded-lg px-6">
                <AccordionTrigger className="text-left hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-primary" />
                    <span className="font-semibold">How do I book an appointment?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-muted-foreground leading-relaxed">
                  Booking is easy and convenient:
                  <ul className="mt-3 space-y-2 ml-4">
                    <li>• <strong>WhatsApp:</strong> Message us at 069 552 3598 for instant booking</li>
                    <li>• <strong>Phone:</strong> Call us directly at 069 552 3598</li>
                    <li>• <strong>Walk-in:</strong> Visit us at 100 Market Street, Office No. 12, Amie Bava Square</li>
                  </ul>
                  We recommend booking in advance to secure your preferred time slot.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="border border-border rounded-lg px-6">
                <AccordionTrigger className="text-left hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-primary" />
                    <span className="font-semibold">What are your operating hours?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-muted-foreground leading-relaxed">
                  We're open to accommodate your busy schedule:
                  <ul className="mt-3 space-y-2 ml-4">
                    <li>• <strong>Monday - Friday:</strong> 9:00 AM - 5:00 PM</li>
                    <li>• <strong>Saturday:</strong> 9:00 AM - 1:00 PM</li>
                    <li>• <strong>Sunday & Holidays:</strong> Closed</li>
                  </ul>
                  We offer flexible appointment times within our operating hours to suit your convenience.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="border border-border rounded-lg px-6">
                <AccordionTrigger className="text-left hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Zap className="h-5 w-5 text-primary" />
                    <span className="font-semibold">How does body contouring work and is it safe?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-muted-foreground leading-relaxed">
                  Our body contouring treatments use advanced, non-invasive technologies:
                  <ul className="mt-3 space-y-2 ml-4">
                    <li>• <strong>Cavitation:</strong> Uses ultrasound to break down fat cells naturally</li>
                    <li>• <strong>RF (Radio Frequency):</strong> Tightens skin and stimulates collagen production</li>
                    <li>• <strong>Laser:</strong> Targets specific areas for precise fat reduction</li>
                    <li>• <strong>Vacuum Bipolar RF:</strong> Combines suction with RF for enhanced results</li>
                  </ul>
                  All treatments are FDA-approved, non-surgical, and performed by qualified professionals with strict safety protocols.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="border border-border rounded-lg px-6">
                <AccordionTrigger className="text-left hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Heart className="h-5 w-5 text-primary" />
                    <span className="font-semibold">How many sessions will I need to see results?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-muted-foreground leading-relaxed">
                  Results vary based on individual goals and treatment type:
                  <ul className="mt-3 space-y-2 ml-4">
                    <li>• <strong>Body Contouring:</strong> Visible results after 1-2 sessions, optimal results after 6-8 sessions</li>
                    <li>• <strong>IV Therapy:</strong> Immediate energy boost, cumulative benefits with regular sessions</li>
                    <li>• <strong>Massage:</strong> Immediate relaxation, long-term benefits with regular treatments</li>
                    <li>• <strong>Weight Loss:</strong> Gradual, sustainable results over 3-6 months</li>
                  </ul>
                  We'll create a personalized treatment plan during your consultation.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="border border-border rounded-lg px-6">
                <AccordionTrigger className="text-left hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Are there any side effects or contraindications?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-muted-foreground leading-relaxed">
                  Our treatments are generally safe with minimal side effects:
                  <ul className="mt-3 space-y-2 ml-4">
                    <li>• <strong>Body Contouring:</strong> Mild redness or warmth that subsides within hours</li>
                    <li>• <strong>IV Therapy:</strong> Rare minor bruising at injection site</li>
                    <li>• <strong>Massage:</strong> Temporary muscle soreness (normal response)</li>
                  </ul>
                  <strong>Contraindications include:</strong> Pregnancy, certain medical conditions, pacemakers (for RF treatments). 
                  We conduct thorough consultations to ensure treatments are suitable for you.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7" className="border border-border rounded-lg px-6">
                <AccordionTrigger className="text-left hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Do I need to prepare for my treatment?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-muted-foreground leading-relaxed">
                  Preparation varies by treatment type:
                  <ul className="mt-3 space-y-2 ml-4">
                    <li>• <strong>Body Contouring:</strong> Stay hydrated, avoid alcohol 24 hours before</li>
                    <li>• <strong>IV Therapy:</strong> Eat a light meal beforehand, stay hydrated</li>
                    <li>• <strong>Massage:</strong> Avoid heavy meals 2 hours before, arrive relaxed</li>
                    <li>• <strong>All treatments:</strong> Wear comfortable clothing, arrive 10 minutes early</li>
                  </ul>
                  We'll provide specific preparation instructions when you book your appointment.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-8" className="border border-border rounded-lg px-6">
                <AccordionTrigger className="text-left hover:no-underline">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <span className="font-semibold">What should I expect during my first visit?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-muted-foreground leading-relaxed">
                  Your first visit includes:
                  <ul className="mt-3 space-y-2 ml-4">
                    <li>• <strong>Welcome & Check-in:</strong> Comfortable reception area with secured parking</li>
                    <li>• <strong>Comprehensive Consultation:</strong> Health assessment and goal discussion</li>
                    <li>• <strong>Treatment Plan:</strong> Personalized recommendations based on your needs</li>
                    <li>• <strong>Facility Tour:</strong> See our modern, hygienic treatment rooms</li>
                    <li>• <strong>First Treatment:</strong> Begin your wellness journey (if desired)</li>
                  </ul>
                  We ensure you feel comfortable and informed throughout your visit.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-9" className="border border-border rounded-lg px-6">
                <AccordionTrigger className="text-left hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Heart className="h-5 w-5 text-primary" />
                    <span className="font-semibold">What makes Glow by Precious different from other wellness centers?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-muted-foreground leading-relaxed">
                  We stand out through:
                  <ul className="mt-3 space-y-2 ml-4">
                    <li>• <strong>Advanced Technology:</strong> Latest FDA-approved equipment and techniques</li>
                    <li>• <strong>Professional Team:</strong> Qualified, experienced practitioners</li>
                    <li>• <strong>Personalized Care:</strong> Tailored treatment plans for individual needs</li>
                    <li>• <strong>Comprehensive Services:</strong> Full range of beauty, health, and wellness treatments</li>
                    <li>• <strong>Convenient Location:</strong> Central Polokwane with secured parking</li>
                    <li>• <strong>Safety First:</strong> Strict hygiene protocols and safety standards</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-10" className="border border-border rounded-lg px-6">
                <AccordionTrigger className="text-left hover:no-underline">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-primary" />
                    <span className="font-semibold">What is your cancellation policy?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-muted-foreground leading-relaxed">
                  Our cancellation policy ensures fair scheduling for all clients:
                  <ul className="mt-3 space-y-2 ml-4">
                    <li>• <strong>24-Hour Notice:</strong> Please cancel or reschedule at least 24 hours in advance</li>
                    <li>• <strong>Same-Day Cancellations:</strong> May incur a cancellation fee</li>
                    <li>• <strong>No-Shows:</strong> Full treatment fee may be charged</li>
                    <li>• <strong>Emergency Situations:</strong> We understand and will work with you</li>
                  </ul>
                  Contact us via WhatsApp or phone to cancel or reschedule your appointment.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-11" className="border border-border rounded-lg px-6">
                <AccordionTrigger className="text-left hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-primary" />
                    <span className="font-semibold">How do you ensure hygiene and safety?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-muted-foreground leading-relaxed">
                  We maintain the highest standards of hygiene and safety:
                  <ul className="mt-3 space-y-2 ml-4">
                    <li>• <strong>Equipment Sterilization:</strong> All equipment sanitized between clients</li>
                    <li>• <strong>Disposable Items:</strong> Single-use items where applicable</li>
                    <li>• <strong>Clean Environment:</strong> Treatment rooms cleaned and disinfected regularly</li>
                    <li>• <strong>Professional Standards:</strong> Staff trained in hygiene protocols</li>
                    <li>• <strong>Health Screening:</strong> Pre-treatment health assessments</li>
                    <li>• <strong>Quality Equipment:</strong> Only FDA-approved, professional-grade devices</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-12" className="border border-border rounded-lg px-6">
                <AccordionTrigger className="text-left hover:no-underline">
                  <div className="flex items-center gap-3">
                    <Heart className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Do you offer package deals or special offers?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-4 pb-6 text-muted-foreground leading-relaxed">
                  Yes! We offer various packages and specials:
                  <ul className="mt-3 space-y-2 ml-4">
                    <li>• <strong>Treatment Packages:</strong> Combine multiple services for better value</li>
                    <li>• <strong>First-Time Client Special:</strong> Exclusive introductory offers</li>
                    <li>• <strong>Monthly Specials:</strong> Rotating promotional offers</li>
                    <li>• <strong>Loyalty Program:</strong> Rewards for regular clients</li>
                    <li>• <strong>Referral Discounts:</strong> Benefits for referring friends</li>
                  </ul>
                  Check our Special Offers page or contact us for current promotions.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-20 bg-gradient-soft">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-8 max-w-3xl mx-auto">
            <div className="space-y-4">
              <h2 className="text-section text-foreground">Still Have Questions?</h2>
              <p className="text-lg text-muted-foreground">
                Our friendly team is here to help you with any additional questions or concerns you may have.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0">
                <CardContent className="p-6 text-center space-y-4">
                  <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center mx-auto">
                    <MessageCircle className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground">WhatsApp Us</h3>
                  <p className="text-sm text-muted-foreground">Quick responses to your questions</p>
                  <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                    <Button className="gradient-primary text-white w-full">
                      Message Now
                    </Button>
                  </a>
                </CardContent>
              </Card>
              
              <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0">
                <CardContent className="p-6 text-center space-y-4">
                  <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center mx-auto">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground">Call Us</h3>
                  <p className="text-sm text-muted-foreground">Speak directly with our team</p>
                  <a href="tel:0695523598">
                    <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white w-full">
                      Call Now
                    </Button>
                  </a>
                </CardContent>
              </Card>
              
              <Card className="shadow-soft hover:shadow-elegant transition-all duration-300 border-0">
                <CardContent className="p-6 text-center space-y-4">
                  <div className="w-12 h-12 gradient-primary rounded-full flex items-center justify-center mx-auto">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-foreground">Visit Us</h3>
                  <p className="text-sm text-muted-foreground">Come for a consultation</p>
                  <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white w-full">
                    Get Directions
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-600/20 to-purple-600/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <h2 className="text-section text-white">Ready to Start Your Wellness Journey?</h2>
              <p className="text-xl text-pink-100 max-w-2xl mx-auto">
                Don't let questions hold you back. Book your consultation today and let us guide you 
                through your transformation journey.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a href="https://wa.me/27695523598" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-white text-primary hover:bg-pink-50 shadow-elegant hover:shadow-lg transition-all duration-300 text-lg px-8 py-4 w-full sm:w-auto">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Book Consultation
                </Button>
              </a>
              <a href="tel:0695523598">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-primary transition-smooth text-lg px-8 py-4 w-full sm:w-auto">
                  <Phone className="h-5 w-5 mr-2" />
                  Call: 069 552 3598
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;